import React, { useState, useRef, useCallback, useEffect, useContext } from "react";
import {
  Input,
  InputGroup,
  InputLeftElement,
  Icon,
  Flex,
  Progress,
  Text,
  Box,
  Tag,
  TagLabel,
  TagLeftIcon
} from "@chakra-ui/react";
import { MdDevices, MdFace, MdOutlineDevices, MdOutlineFace, MdOutlineLayers, MdOutlineRoute, MdOutlineSentimentSatisfied, MdOutlineSentimentVerySatisfied, MdSearch } from "react-icons/md";
import useExperienceApi from "../experiences/lib/useExperienceApi";
import useInsightApi from "../insight/lib/useInsightApi";
import usePersonaApi from "../persona/lib/usePersonaApi";
import useProductsApi from "../products/lib/useProductsApi";
import { useSharedExperiencesApi } from "../shared-experiences/lib/useSharedExperiencesApi";
import { Link, useNavigate } from "react-router-dom";
import InsightPicto from "../insight/components/insightPicto";
import PersonaThumbnail from "../persona/components/personaThumbnail";
import PersonaProvider from "../persona/lib/personaProvider";
import ProductProvider from "../products/lib/productProvider";
import ProductLogo from "../products/components/productLogo";
import ProfileProvider from "../profile/lib/profileProvider";
import ProfileThumbnail from "../profile/component/profileThumbnail";
import { AuthContext } from "../auth/lib/authContext";
import SharedExperienceIcon from "./icons/sharedExperienceIcon";
import useTranslation from "../lib/i18n/useTranslation";
const SearchBar = () => {
  const [suggestions, setSuggestions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isFocus, setIsFocus] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef(null);
  const experienceApi = useExperienceApi();
  const insightApi = useInsightApi();
  const personaApi = usePersonaApi();
  const productApi = useProductsApi();
  const sharedExperiencesApi = useSharedExperiencesApi();
  const [menuHover, setMenuHover] = useState(false);
  const [inputHover, setInputHover] = useState(false);
  const [filter, setFilter] = useState('all');
  const { t } = useTranslation();
  const handleSearch = async (event) => {
    let value;
    if (event) {
      value = event.target.value;
      setSearchTerm(value);
    } else {
      value = searchTerm;
    }
    setIsLoading(true);
    if (value && value.length >= 2) {
      let newSuggestions;
      if (filter === 'all') {
        const promises = [
          productApi.searchProducts(value),
          personaApi.searchManyPersona(value),
          experienceApi.searchProductExperienceGroups(value),
          experienceApi.searchPersonaExperienceGroups(value),
          experienceApi.searchExperiences(value),
          insightApi.searchInsights(value),
          sharedExperiencesApi.searchSharedExperiences(value),
        ]
        let  result = await Promise.all(promises);
        result = result.map(r => (r ? r : []) );
        newSuggestions =
          result[0].map((r) => ({
            type: 'product',
            data: r
          })).concat(
            result[1].map((r) => ({
              type: 'persona',
              data: r
            }))).concat(
              result[2].map((r) => ({
                type: 'product_experience_group' ,
                data: r
              }))).concat(
                result[3].map((r) => ({
                  type: 'persona_experience_group' ,
                  data: r
                }))).concat(
                  result[4].map((r) => ({
                    type: 'experience',
                    data: r
                  }))).concat(
                    result[5].map((r) => ({
                      type: 'insight',
                      data: r
                    }))).concat(
                      result[6].map((r) => ({
                        type: 'shared_experience',
                        data: r
                      })))
      } else if (filter === 'persona') {
        const personaData = await personaApi.searchManyPersona(value);
        newSuggestions = (personaData || []).map((pd) => {
          return {
            type: 'persona',
            data: pd
          }
        })
      } else if (filter === 'product') {
        const products = await productApi.searchProducts(value)
        newSuggestions = products.map((p) => {
          return {
            type: 'product',
            data: p
          }
        })
      } else if (filter === 'insight') {
        const insights = await insightApi.searchInsights(value);
        newSuggestions = insights.map((i) => {
          return {
            type: 'insight',
            data: i
          }
        })
      } else if (filter === 'experience') {
        const promises = [
          experienceApi.searchExperiences(value),
          experienceApi.searchPersonaExperienceGroups(value),
          experienceApi.searchProductExperienceGroups(value),
        ]
        const result = await Promise.all(promises);
        newSuggestions = result[0].map((r) => ({
          type: 'experience',
          data: r
        })).concat(
          result[1].map((r) => ({
            type: 'persona_experience_group',
            data: r
          }))).concat(
            result[2].map((r) => ({
              type: 'product_experience_group',
              data: r
            })));
      } else if (filter === 'shared_experience') {
        const sharedExperiences = await sharedExperiencesApi.searchSharedExperiences(value);
        newSuggestions = (sharedExperiences || []).map((se) => ({
          type: 'shared_experience',
          data: se
        }));
      }
      setSuggestions(newSuggestions)
    } else {
      setSuggestions([]);
    }
    setIsLoading(false);
  };
  const handleClick = useCallback(() => {
    const newIsFocus = document.activeElement === inputRef.current;
    setIsFocus(newIsFocus);
    if (!menuHover && !inputHover && !newIsFocus) {
      setIsOpen(false);
    }
  }, []);
  const handleFocus = useCallback(() => {
    setIsOpen(true);
  }, []);
  const handleKey = useCallback(
    (e) => {
      if(e.key === 'Escape' && isOpen) {
        setIsFocus(false)
        setIsOpen(false)
        const newIsFocus = inputRef.current.blur();
        setSuggestions([])
        setSearchTerm("")
      }
    },
    [isOpen]
  );
  useEffect(() => {
    const clickEvent = window.addEventListener('click', handleClick);
    return () => {
      window.removeEventListener('click', clickEvent);
    }
  }, [])
  useEffect(() => {
    window.addEventListener("keydown", handleKey);
    return () => {
      window.removeEventListener("keydown", handleKey);
    };
  }, [handleKey]);
  useEffect(() => {
    handleSearch();
  }, [filter])
  return (
    <Flex
      maxW={'500px'}
      width={(isFocus ? "100%" : '300px')}
      mx={3}
      position={"relative"}>
      <InputGroup
        onMouseEnter={() => {
          setInputHover(true)
        }}
        onMouseLeave={() => {
          setInputHover(false)
        }}
      >
        <InputLeftElement pointerEvents="none">
          <Icon as={MdSearch} color="text.medium.grey" />
        </InputLeftElement>
        <Input
          type="text"
          placeholder={t('search.placeholder')}
          value={searchTerm}
          onChange={handleSearch}
          onFocus={handleFocus}
          ref={inputRef}
        />
      </InputGroup>
      {isOpen && (
        <Flex
          position={"absolute"}
          top={"calc(100% + 10px)"}
          backgroundColor={"white"}
          width={'100%'}
          flexDirection={"column"}
          zIndex={"1000"}
          maxHeight={"calc(100vh - 100px)"}
          overflow={"auto"}
          borderRadius={"2px"}
          border={"1px solid"}
          borderColor={"divider.grey"}
          boxShadow='0px 0px 6px 0px rgba(163,163,163,0.15)'
        >
          {isLoading && (
            <Progress isIndeterminate size='xs' opacity={isLoading ? '1' : '0'} />
          )}
          {(searchTerm && searchTerm.length >= 2) && (
            <Flex
              gap={1}
              px={3}
              py={1}
              flexWrap={"wrap"}
              borderBottom={"1px solid"}
              borderColor={"divider.grey"}
            >
              <Tag
                variant={(filter === 'all' ? 'greyDark' : 'grey')}
                cursor={"pointer"}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation()
                  setFilter('all')
                }}
              >{t('search.all')}
              </Tag>
              <Tag
                variant={(filter === 'persona' ? 'greyDark' : 'grey')}
                cursor={"pointer"}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation()
                  setFilter('persona')
                }}
              >
                <TagLeftIcon boxSize='12px' as={MdOutlineFace} />
                <TagLabel>{t('search.persona')}</TagLabel>
              </Tag>
              <Tag
                variant={(filter === 'product' ? 'greyDark' : 'grey')}
                cursor={"pointer"}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation()
                  setFilter('product')
                }}
              >
                <TagLeftIcon boxSize='12px' as={MdOutlineDevices} />
                <TagLabel>{t('search.product')}</TagLabel>
              </Tag>
              <Tag
                variant={(filter === 'experience' ? 'greyDark' : 'grey')}
                cursor={"pointer"}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation()
                  setFilter('experience')
                }}
              >
                <TagLeftIcon boxSize='12px' as={MdOutlineRoute} />
                <TagLabel>{t('search.experienceGroup')}</TagLabel>
              </Tag>
              <Tag
                variant={(filter === 'insight' ? 'greyDark' : 'grey')}
                cursor={"pointer"}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation()
                  setFilter('insight')
                }}
              >
                <TagLeftIcon boxSize='12px' as={MdOutlineSentimentVerySatisfied} />
                <TagLabel>{t('search.insight')}</TagLabel>
              </Tag>
              <Tag
                variant={(filter === 'shared_experience' ? 'greyDark' : 'grey')}
                cursor={"pointer"}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation()
                  setFilter('shared_experience')
                }}
              >
                <TagLeftIcon boxSize='12px' as={MdOutlineLayers} />
                <TagLabel>{t('search.sharedExperience')}</TagLabel>
              </Tag>
            </Flex>
          )}
          <Flex
            flexDirection={"column"}
            py={2}
            onMouseEnter={() => {
              setMenuHover(true)
            }}
            onMouseLeave={() => {
              setMenuHover(false)
            }}
          >
            {suggestions.length > 0 ? suggestions.map((suggestion, i) => (
              <Box
                key={i}
                borderBottom={"1px solid"}
                borderColor={"divider.grey"}
                onClick={() => { setIsOpen(false) }}
              >
                {suggestion.type === 'insight' && <SearchListItemInsight insight={suggestion.data} />}
                {suggestion.type === 'experience' && <SearchListItemExperience experience={suggestion.data} />}
                {suggestion.type === 'persona_experience_group' && <SearchListItemPersonaExperienceGoup group={suggestion.data} />}
                {suggestion.type === 'product_experience_group' && <SearchListItemProductExperienceGoup group={suggestion.data} />}
                {suggestion.type === 'persona' && <SearchListItemPersona data={suggestion.data} />}
                {suggestion.type === 'product' && <SearchListItemProduct product={suggestion.data} />}
                {suggestion.type === 'shared_experience' && <SearchListItemSharedExperience sharedExperience={suggestion.data} />}
              </Box>
            ))
              :
              <Flex px={3} py={2}>
                <Text>{t('search.noResultFound')}</Text>
              </Flex>
            }
          </Flex>
        </Flex>
      )}
    </Flex>
  );
};
const SearchListItemProduct = (props) => {
  const { product } = props;
  const authContext = useContext(AuthContext);
  const { t } = useTranslation();
  return (
    <ProductProvider
      originalProduct={product}
      productId={product.id}
      needRights={false}
      needRole={false}
      needEditionRequest={false}
    >
      <Link to={`/products/${product.id}`}>
        <Flex
          px={3}
          py={2}
          cursor={"pointer"}
          _hover={{ bg: "background.grey" }}
          alignItems={"center"}
          justifyContent={"space-between"}
        >
          <Flex
            flexDirection={"column"}
            flex={1}
          >
            <Flex gap={1} alignItems={"center"} mb={1} color="text.medium.grey">
              <Icon
                as={MdDevices}
                color="text.medium.grey"
                boxSize="16px"
              />
              <Text>{t('search.product')}</Text>
            </Flex>
            <Flex gap={1}>
              <ProductLogo size={20} />
              <Text>{product.name}</Text>
            </Flex>
          </Flex>
          {(product.owner && authContext.profile.id !== product.owner.id) &&
            <ProfileProvider
              profileId={product.owner.id}
              originalProfile={product.owner}
            >
              <Flex gap={2} flexDirection={"column"} alignItems={"flex-end"}>
                <ProfileThumbnail size={25} />
                <Text fontSize={"12px"} color={"text.medium.grey"}>{product.owner.pseudo}</Text>
              </Flex>
            </ProfileProvider>
          }
        </Flex>
      </Link>
    </ProductProvider>
  );
};
const SearchListItemPersona = (props) => {
  const { data } = props;
  const authContext = useContext(AuthContext);
  const { t } = useTranslation();
  return (
    <PersonaProvider
      originalPersona={data.persona}
      personaId={data.persona.id}
      needRights={false}
      needRole={false}
      needEditionRequest={false}
    >
      <Link to={`/persona/${data.persona.id}`}>
        <Flex
          px={3}
          py={2}
          cursor={"pointer"}
          _hover={{ bg: "background.grey" }}
          alignItems={"center"}
          justifyContent={"space-between"}
        >
          <Flex
            flexDirection={"column"}
            flex={1}
          >
            <Flex gap={1} alignItems={"center"} mb={1} color="text.medium.grey">
              <Icon
                as={MdFace}
                color="text.medium.grey"
                boxSize="16px"
              />
              <Text>{t('search.persona')}</Text>
            </Flex>
            <Flex gap={1}>
              <PersonaThumbnail size={20} />
              <Text>{data.persona.name}</Text>
            </Flex>
          </Flex>
          {(data.owner && authContext.profile.id !== data.owner.id) &&
            <ProfileProvider
              profileId={data.owner.id}
              originalProfile={data.owner}
            >
              <Flex gap={2} flexDirection={"column"} alignItems={"flex-end"}>
                <ProfileThumbnail size={25} />
                <Text fontSize={"12px"} color={"text.medium.grey"}>{data.owner.pseudo}</Text>
              </Flex>
            </ProfileProvider>
          }
        </Flex>
      </Link>
    </PersonaProvider>
  )
};
const SearchListItemProductExperienceGoup = (props) => {
  const { group } = props;
  const { t } = useTranslation();
  return (
    <Link to={`/products/${group.product_id}?group-id=${group.id}`}>
      <Flex
        px={3}
        py={2}
        cursor={"pointer"}
        _hover={{ bg: "background.grey" }}
        flexDirection={"column"}
      >
        <Flex gap={1} alignItems={"center"} mb={1} color="text.medium.grey">
          <Icon
            as={MdOutlineLayers}
            color="text.medium.grey"
            boxSize="16px"
          />
          <Text>{t('search.experienceGroup')}</Text>
        </Flex>
        <Text>{group.name}</Text>
      </Flex>
    </Link>
  )
};
const SearchListItemPersonaExperienceGoup = (props) => {
  const { group } = props;
  const { t } = useTranslation();
  return (
    <Link to={`/persona/${group.persona_id}?group-id=${group.id}`}>
      <Flex
        px={3}
        py={2}
        cursor={"pointer"}
        _hover={{ bg: "background.grey" }}
        flexDirection={"column"}
      >
        <Flex gap={1} alignItems={"center"} mb={1} color="text.medium.grey">
          <Icon
            as={MdOutlineLayers}
            color="text.medium.grey"
            boxSize="16px"
          />
          <Text>{t('search.experienceGroup')}</Text>
        </Flex>
        <Text>{group.name}</Text>
      </Flex>
    </Link>
  )
};
const SearchListItemExperience = (props) => {
  const { experience } = props;
  const { t } = useTranslation();
  return (
    <Link to={`/experiences/${experience.id}`}>
      <Flex
        px={3}
        py={2}
        cursor={"pointer"}
        _hover={{ bg: "background.grey" }}
        flexDirection={"column"}
      >
        <Flex
          gap={1}
          alignItems={"center"}
          mb={1}
          color="text.medium.grey">
          <Icon
            as={MdOutlineRoute}
            color="text.medium.grey"
            boxSize="16px"
          />
          <Text >{t('search.experience')}</Text>
        </Flex>
        <Text>{experience.objective}</Text>
      </Flex>
    </Link>
  )
};
const SearchListItemInsight = (props) => {
  const { insight } = props;
  const { t } = useTranslation();
  return (
    <Link to={`/experiences/${insight.experience_id}#${insight.id}`}>
      <Flex
        px={3}
        py={2}
        cursor={"pointer"}
        _hover={{ bg: "background.grey" }}
        flexDirection={"column"}
      >
        <Flex gap={1} alignItems={"center"} mb={1} color="text.medium.grey">
          <InsightPicto size={20} type={insight.type} />
          <Text>{t('search.insight')}</Text>
        </Flex>
        <Text noOfLines={3} >
            {insight.name}
          </Text>
      </Flex>
    </Link>
  )
};
const SearchListItemSharedExperience = (props) => {
  const { sharedExperience } = props;
  const se = sharedExperience.shared_experience || sharedExperience;
  const owner = sharedExperience.owner;
  const authContext = useContext(AuthContext);
  const { t } = useTranslation();
  return (
    <Link to={`/shared-experiences/${se.id}`}>
      <Flex
        px={3}
        py={2}
        cursor={"pointer"}
        _hover={{ bg: "background.grey" }}
        alignItems={"center"}
        justifyContent={"space-between"}
      >
        <Flex
          flexDirection={"column"}
          flex={1}
        >
          <Flex gap={1} alignItems={"center"} mb={1} color="text.medium.grey">
            <Icon
              as={MdOutlineLayers}
              color="text.medium.grey"
              boxSize="16px"
            />
            <Text>{t('search.sharedExperience')}</Text>
          </Flex>
          <Text>{se.name}</Text>
        </Flex>
        {(owner && authContext.profile.id !== owner.id) &&
          <ProfileProvider
            profileId={owner.id}
            originalProfile={owner}
          >
            <Flex gap={2} flexDirection={"column"} alignItems={"flex-end"}>
              <ProfileThumbnail size={25} />
              <Text fontSize={"12px"} color={"text.medium.grey"}>{owner.pseudo}</Text>
            </Flex>
          </ProfileProvider>
        }
      </Flex>
    </Link>
  );
};
export default SearchBar;
